/**
 * The Character Studio clip clock's one step, as a pure function.
 *
 * The transport advances by the time between animation frames. That time is
 * not guaranteed to be positive or small: `requestAnimationFrame` hands its
 * callback the frame's start time, which can be earlier than the
 * `performance.now()` the loop was started with, and a stalled or background
 * tab can deliver a gap of seconds. Added straight on, the first made the
 * clock run backwards (the scrubber read "-2.49 of 2.00 seconds" on a loaded
 * machine) and the second skipped a looping clip through several cycles at
 * once. So a step is never negative, never larger than `MAX_CLIP_STEP`, and
 * the result always lies between the clip's first and last frame.
 */

/** The longest single step the clock takes, seconds: a stalled frame resumes instead of skipping. */
export const MAX_CLIP_STEP = 0.25;

export interface ClipStep {
  /** The clip time after the step, in [0, duration]. */
  time: number;
  /** A non-looping clip reached its last frame on this step. */
  ended: boolean;
}

/**
 * Advance a clip's time by `dt` seconds (already scaled by playback speed).
 * A looping clip wraps back into [0, duration); a non-looping one stops at
 * `duration` and reports `ended`. A clip with no duration stays at 0.
 */
export function advanceClipTime(previous: number, dt: number, duration: number, loop: boolean): ClipStep {
  if (!(duration > 0)) return { time: 0, ended: !loop };
  const start = Number.isFinite(previous) ? Math.min(Math.max(previous, 0), duration) : 0;
  const step = Number.isFinite(dt) ? Math.min(Math.max(dt, 0), MAX_CLIP_STEP) : 0;
  const next = start + step;
  if (next < duration) return { time: next, ended: false };
  if (loop) return { time: ((next % duration) + duration) % duration, ended: false };
  return { time: duration, ended: true };
}
